/**
 * Request converter — Heatmap format
 * Phase 2: common.ts decomposition
 *
 * Converts the old-format heatmap request into the new API format.
 */

import { getCropSeasonValue } from '../../../utils/cropSeason';
import {
  toApiCrop,
  toWireKey,
  toUiKey,
  encodeEAMEV2Filters,
  buildRegionFilterFields,
  WIRE,
  buildRmtNRangeFilters
} from '../../apiAdapter';
import { supportsTprFilter } from '../../../config/appConfig';

/**
 * Canonicalize any filter key to its wire snake_case form regardless of the
 * incoming shape — UI key ("genericYield"), wire snake ("generic_yield"), or a
 * legacy short code ("yl"/"chks"). This is what lets the LATAM branches match
 * whether the caller passes decoded (UI) filters or raw response keys.
 */
const toCanonicalWireKey = (key: unknown): string =>
  toWireKey(toUiKey(String((key as string | number | null | undefined) ?? '')));

// ─── Helpers ────────────────────────────────────────────────────────

const convertParametersToFilters = (values: unknown) => {
  if (typeof values === 'string') {
    return values.split(',');
  } else {
    return values;
  }
};

// Filter params reach this converter in two shapes: lowercase key/values (LATAM
// filter objects) and capital Key/Values (EAME getRdata-style params). Read both.
const getFilterKey = (f: any) => f?.key ?? f?.Key;
const getFilterValues = (f: any) => f?.values ?? f?.Values;

// `traits` (scatter) must be an array — split a comma string, pass an array through.
const toTraitArray = (v: unknown): string[] => {
  if (Array.isArray(v)) return v;
  if (typeof v === 'string') return v.split(',');
  return [];
};

// Encode one filter param into newItem.filters. Non-LATAM (and any LATAM key without a
// special shape) fall through to the generic { field, value }. Extracted from the forEach
// so convertHeatMapRequestToNewFormat stays within cognitive-complexity limits — behavior
// is unchanged (v1 LATAM wire shapes preserved).
const applyHeatmapLatamParam = (param: any, newItem: any, item: any): void => {
  const wk = toCanonicalWireKey(param.key ?? param.Key);
  const rawVal = param.values || param.Values;
  if (item.skipFilters?.some((sk: string) => toCanonicalWireKey(sk) === wk)) {
    return;
  }
  const value = rawVal ? convertParametersToFilters(rawVal) : null;
  // The sole call site is already the `else if (region === 'LATAM')` arm of an outer
  // conditional, so this inner check can never be true.
  /* v8 ignore if -- @preserve */
  if (item.region !== 'LATAM') {
    newItem.filters[wk] = { field: wk, value };
    return;
  }
  switch (wk) {
    case WIRE.GENERIC_YIELD:
      newItem.filters[WIRE.YIELD_LVL] = { field: WIRE.YIELD_LVL, value };
      break;
    case WIRE.ENTRIES: {
      const entryFilterKey =
        item.isOrthogonalMode && item.apiType === 'scatterplot' ? WIRE.ENTRIES : WIRE.PRODUCT;
      newItem.filters[entryFilterKey] = { field: item.entryType.toLowerCase(), value };
      break;
    }
    case WIRE.AVG_YGSMN: {
      // LATAM yield ranges → split _from/_to objects with numeric values.
      // avg_ygsmn uses `average_ygsmn_*` keys while keeping field `avg_ygsmn`.
      const vals = convertParametersToFilters(rawVal);
      newItem.filters.average_ygsmn_from = { field: WIRE.AVG_YGSMN, value: Number(vals[0]) };
      newItem.filters.average_ygsmn_to = { field: WIRE.AVG_YGSMN, value: Number(vals[1]) };
      break;
    }
    case WIRE.YGSMN: {
      const vals = convertParametersToFilters(rawVal);
      newItem.filters.ygsmn_from = { field: WIRE.YGSMN, value: Number(vals[0]) };
      newItem.filters.ygsmn_to = { field: WIRE.YGSMN, value: Number(vals[1]) };
      break;
    }
    case WIRE.TEMP_PRECIP_RAW:
      newItem.filters[WIRE.TEMP_PRECIP_RATIO_RANGE] = {
        field: WIRE.TEMP_PRECIP_RAW,
        value: convertParametersToFilters(param.values),
        is_no_data_check_enabled: true
      };
      break;
    case WIRE.RMT_N:
      // Heatmap/scatter keep their existing { value } wire shape (no `field`)
      Object.assign(
        newItem.filters,
        buildRmtNRangeFilters(convertParametersToFilters(rawVal), { includeField: false })
      );
      break;
    case 'enso':
      // The backend ENSO filter column is enso_phase (per-phase breakdown), not the
      // bucket key 'enso'. Payload key stays 'enso'; only the inner field name changes.
      newItem.filters[wk] = { field: 'enso_phase', value };
      break;
    default:
      newItem.filters[wk] = { field: wk, value };
  }
};

// x_trait / y_trait / trait axis fields (heatmap + scatter), merged into one object to
// spread into the request. Extracted to keep convertHeatMapRequestToNewFormat within
// cognitive-complexity limits.
const buildTraitAxisFields = (filters: any[], traitKey: string): Record<string, unknown> => {
  const fields: Record<string, unknown> = {};
  const xTraitItem = filters.find((f: any) => getFilterKey(f) === WIRE.X_TRAIT);
  if (xTraitItem) fields.x_trait = getFilterValues(xTraitItem) ?? '';
  const yTraitItem = filters.find((f: any) => getFilterKey(f) === WIRE.Y_TRAIT);
  if (yTraitItem) fields.y_trait = getFilterValues(yTraitItem) ?? '';
  const traitItem = filters.find(
    (f: any) => getFilterKey(f) === WIRE.TRAIT || getFilterKey(f) === WIRE.TRAITS
  );
  if (traitItem) {
    fields[traitKey] =
      traitKey === WIRE.TRAIT
        ? getFilterValues(traitItem)
        : toTraitArray(getFilterValues(traitItem));
  }
  return fields;
};

// LATAM encodes the season into the crop; EAME uses the crop alias.
const buildHeatmapCrop = (item: any, season: string | null): string => {
  if (item.region !== 'LATAM') return toApiCrop(item.crop);
  return season ? (item.crop + season).toUpperCase() : item.crop;
};

// ─── Public API ─────────────────────────────────────────────────────

export const convertHeatMapRequestToNewFormat = (item: any) => {
  const { traitKey = 'trait' } = item;
  const season = getCropSeasonValue(item.filters) ?? null;
  const minNoOfLocItem = item.filters.find((f: any) => {
    const k = f?.key ?? f?.Key;
    return k === 'min_no_of_locations' || toWireKey(k) === 'min_no_of_locations';
  });
  const minNoOfLocations = minNoOfLocItem?.values ?? minNoOfLocItem?.Values ?? 0;
  const traitAxisFields = buildTraitAxisFields(item.filters, traitKey);

  // EAME v2 top-level region-filter fields (heatmap + scatter). tpr_filter_type is
  // only for TPR-supported crops (e.g. corn); is_region_filter_supported is set when
  // the country is empty (any crop) or a custom threshold is used. LATAM is unaffected.
  const isEAME = item.region !== 'LATAM';
  const isCustomThreshold = item.thresholdType === 'custom';
  const tprFilterItem = item.filters.find(
    (f: any) => toCanonicalWireKey(getFilterKey(f)) === WIRE.TPR_FILTER_TYPE
  );
  const tprFilterValue =
    isEAME && supportsTprFilter(String(item.crop ?? '').toLowerCase())
      ? (getFilterValues(tprFilterItem) ?? null)
      : null;
  const regionFilterFields = isEAME
    ? buildRegionFilterFields({
        country: item.country,
        isCustomThreshold,
        tprFilterValue
      })
    : {};

  // v2 (EAME) omits country when empty/undefined (region-level); v1 (LATAM) keeps it as null.
  const countryContext = (() => {
    if (item.region === 'LATAM') return { country: item.country ?? null };
    if (item.country) return { country: item.country };
    return {};
  })();

  const newItem: any = {
    crop: buildHeatmapCrop(item, season),
    region: item.region,
    context: {
      entry: item.entry,
      entry_type: item.entryType,
      // v2 (EAME): omit country when empty/undefined (region-level). v1 (LATAM): unchanged.
      ...countryContext,
      // Country zone (e.g. selected for France) — include when present.
      ...(item.countryZone ? { country_zone: item.countryZone } : {})
    },
    filters: {},
    min_no_of_locations: minNoOfLocations,
    ...traitAxisFields,
    is_export_raw_data: item.isExportRawData ?? false,
    busy_loading: item.busyLoading ?? false,
    is_orthogonal_mode: item.isOrthogonalMode ?? false,
    unit_type: item.unitAliase[item.userYieldUnit] ?? item.unitAliase['t/ha'],
    ...regionFilterFields
  };
  if (!item.isDefault && item.filters.length > 0 && item.region !== 'LATAM') {
    // EAME v2: native filter shape (generic_yield / entries / range split / { value }).
    // tpr_filter_type is a top-level field only — never inside the filters object.
    // Pass tpr/custom-threshold so generic_yield is emitted as { value: [{start,end}] }
    // ranges (same shape as entry-performance), not raw "7-10" strings.
    newItem.filters = encodeEAMEV2Filters(item.filters, item.entryType, {
      skipUiKeys: [...(item.skipFilters ?? []), WIRE.TPR_FILTER_TYPE],
      regionFilterTprZone: tprFilterValue,
      isCustomThreshold
    });
  } else if (!item.isDefault && item.filters.length > 0) {
    item.filters.forEach((param: any) => applyHeatmapLatamParam(param, newItem, item));
  }
  return newItem;
};
